<template>
  <BaseWidget
    title="Plans ending soon"
    :show-info-icon="true"
    info-tooltip="Plans with end dates coming up within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/2056112-what-is-a-plan', text: 'Learn about Plans' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    custom-class="plans-ending-soon-widget"
    :date-range="dateRange"
    :future-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading && !data"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      Error loading plans: {{ error.message }}
    </div>
    <template v-else>
      <div
        ref="widgetRef"
        :class="['widget-content-wrapper', { 'focused': focused }]"
      >
        <div class="plan-count">
          <div class="count-row">
            <span class="count">{{ planCount }}</span>
            <ComparisonChip 
              :change="changePercentage" 
              :previous-value="formattedPreviousCount"
              :previous-period-dates="previousTimestamps"
              :inverted="true"
              :date-range-preset="dateRange"
              label="Plans ending soon"
            />
          </div>
          <div class="count-label-row">
            <span class="count-label">{{ dateRangeLabel }}</span>
            <a
              v-if="plans.length > 0"
              class="focused-view-all-link"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
        </div>
        
        <div
          v-if="!focused"
          class="plans-list"
        >
          <div 
            v-for="(plan, index) in displayPlans" 
            :key="plan.id || index" 
            class="plan-item"
            @click="handlePlanClickGuarded(plan)"
          >
            <div class="plan-row">
              <div class="plan-image">
                <img 
                  v-if="plan.payerAccount?.avatar" 
                  :src="plan.payerAccount.avatar" 
                  :alt="plan.payerAccount?.name || plan.name"
                >
                <div 
                  v-else 
                  class="image-placeholder"
                  :style="{ 
                    background: plan.pastelColor || PASTEL_COLORS[0].bg,
                    color: plan.pastelTextColor || PASTEL_COLORS[0].text
                  }"
                >
                  {{ getInitials(plan.payerAccount?.name || plan.name) }}
                </div>
              </div>
              <div class="plan-details">
                <div class="plan-name">
                  {{ plan.name }}
                </div>
                <div class="plan-meta">
                  <span class="plan-time">{{ formatPlanTime(plan) }}</span>
                  <span
                    v-if="plan.payerAccount?.name"
                    class="plan-meta-sep"
                  > • </span>
                  <span
                    v-if="plan.payerAccount?.name"
                    class="plan-account-name"
                  >{{ plan.payerAccount.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="displayPlans.length === 0 && plans.length === 0 && !hasMorePlans"
            class="no-plans"
          >
            No plans ending soon
          </div>
        </div>
      </div>
    </template>
  </BaseWidget>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'

import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import { useAccountPlansData, useGraphQLData } from '../composables/useWidgetData.js'
import { getDemoAccountPlansData } from '../services/demoData.js'
import { getDateRangeTimestamps, getPreviousPeriodTimestamps, DATE_RANGE_LABELS, getDateRangeDates } from '../utils/dateRanges.js'
import { useReferenceDate } from '../composables/useReferenceDate.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 2
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'next_7_days')
const widgetRef = ref(null)

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Next 7 days'
})

// Reference date for range calculations (can be overridden by parent via postMessage or ?reference_date=)
const referenceDate = useReferenceDate()

// Calculate timestamps from date range
const timestamps = computed(() => {
  return getDateRangeTimestamps(dateRange.value, referenceDate.value)
})

// Fetch previous period data for comparison
const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value, referenceDate.value)
})

const accountPlansQuery = `
  query accountPlans($end_timestamp_from: Int!, $end_timestamp_to: Int!) {
    accountPlans(
      order: END_TIMESTAMP_ASC
      end_timestamp_from: $end_timestamp_from
      end_timestamp_to: $end_timestamp_to
    ) {
      total
      found_without_filters
      data {
        account_plan_id
        plan_template {
          image {
            url
          }
        }
        payer_account {
          account_id
          name
          type
          avatar {
            url
          }
        }
        name
        status
        start_timestamp
        end_timestamp
      }
    }
  }
`

const { data, loading, error, cleanup, refresh } = useGraphQLData({
  dataKey: 'plans_ending_soon',
  query: accountPlansQuery,
  variables: computed(() => ({
    end_timestamp_from: timestamps.value.from_timestamp,
    end_timestamp_to: timestamps.value.to_timestamp
  })),
  operationName: 'accountPlans',
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoAccountPlansData('current')
})

const {
  data: previousData, 
  loading: previousLoading, 
  error: previousError, 
  cleanup: previousCleanup,
  refresh: refreshPrevious
} = useGraphQLData({
  dataKey: 'plans_ending_previous',
  query: accountPlansQuery,
  variables: computed(() => ({
    end_timestamp_from: previousTimestamps.value.from_timestamp,
    end_timestamp_to: previousTimestamps.value.to_timestamp
  })),
  operationName: 'accountPlans',
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoAccountPlansData('previous')
})

// Ensure previous data is fetched when current data loads
watch(() => data.value, (newData) => {
  if (newData && !previousLoading.value && !previousData.value) {
    refreshPrevious(true)
  }
}, { immediate: true })

// Pastel color palette extracted from design chips
const PASTEL_COLORS = [
  { bg: '#DBEDDB', text: '#1C3829' }, // Green
  { bg: '#FFE2DD', text: '#5D1715' }, // Red/Pink
  { bg: '#D6E8F5', text: '#1E3A5F' }, // Blue (estimated)
  { bg: '#E8D5E3', text: '#5D2D4F' }, // Purple/Lavender (estimated)
  { bg: '#FFF4D6', text: '#8B6914' }, // Yellow/Gold (estimated)
  { bg: '#E5E5E5', text: '#4A4A4A' }, // Gray (estimated)
  { bg: '#FFE8D1', text: '#8B4513' }, // Peach/Orange (estimated)
  { bg: '#F5F5F5', text: '#333333' }, // Light (estimated)
  { bg: '#F5E6D3', text: '#6B4423' }, // Brown/Beige (estimated)
  { bg: '#FFE5F1', text: '#8B3A62' }  // Pink (estimated)
]

// Generate a pastel color based on a string (plan name or ID)
const getPastelColor = (str) => {
  if (!str) return PASTEL_COLORS[0].bg // Default to first color
  
  // Generate a hash from the string
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  
  // Use hash to select a color from the palette
  const colorIndex = Math.abs(hash) % PASTEL_COLORS.length
  return PASTEL_COLORS[colorIndex].bg
}

// Get text color for a given background color
const getTextColor = (str) => {
  if (!str) return PASTEL_COLORS[0].text
  
  // Generate a hash from the string
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  
  // Use hash to select matching text color from the palette
  const colorIndex = Math.abs(hash) % PASTEL_COLORS.length
  return PASTEL_COLORS[colorIndex].text
}

// Transform account plans data - filter client-side to ensure accuracy
const plans = computed(() => {
  if (!data.value?.accountPlans?.data) {
    return []
  }
  
  const fromTimestamp = timestamps.value.from_timestamp
  const toTimestamp = timestamps.value.to_timestamp
  
  const isCanceled = (s) => ['canceled', 'cancelled'].includes(String(s || '').toLowerCase())
  return data.value.accountPlans.data
    .filter(plan => {
      if (isCanceled(plan.status)) return false
      // Only include plans that have an end_timestamp and it's within the selected range
      if (!plan.end_timestamp) return false
      return plan.end_timestamp >= fromTimestamp && plan.end_timestamp <= toTimestamp
    })
    .map(plan => {
      const planId = plan.account_plan_id
      const planName = plan.name || 'Unnamed Plan'
      const accountRef = plan.payer_account?.account_id || plan.payer_account?.name || planName
      
      return {
        id: planId,
        name: planName,
        startTimestamp: plan.start_timestamp * 1000, // Convert to milliseconds
        endTimestamp: plan.end_timestamp ? plan.end_timestamp * 1000 : null,
        status: plan.status,
        payerAccount: plan.payer_account ? {
          account_id: plan.payer_account.account_id,
          name: plan.payer_account.name || 'Unknown Account',
          type: plan.payer_account.type || 'Unknown',
          avatar: getAvatarUrl(plan.payer_account.avatar?.url, plan.payer_account.name || plan.payer_account.account_id)
        } : null,
        pastelColor: getPastelColor(accountRef), // For placeholder when no account avatar
        pastelTextColor: getTextColor(accountRef)
      }
    })
    .sort((a, b) => a.endTimestamp - b.endTimestamp) // Sort by end time (soonest ending first)
})

// Count plans ending in the selected date range
const planCount = computed(() => {
  return plans.value.length
})

// Count plans ending in previous period - filter client-side to ensure accuracy
const previousCount = computed(() => {
  if (previousData.value === null || previousData.value === undefined) return null
  if (!previousData.value?.accountPlans?.data) return 0
  
  const fromTimestamp = previousTimestamps.value.from_timestamp
  const toTimestamp = previousTimestamps.value.to_timestamp
  
  const isCanceled = (s) => ['canceled', 'cancelled'].includes(String(s || '').toLowerCase())
  // Filter for plans where end_timestamp is actually within the previous period range
  return previousData.value.accountPlans.data.filter(plan => {
    if (isCanceled(plan.status)) return false
    if (!plan.end_timestamp) return false
    return plan.end_timestamp >= fromTimestamp && plan.end_timestamp <= toTimestamp
  }).length
})

// Calculate percentage change
const changePercentage = computed(() => {
  // Don't show comparison while data is loading
  if (loading.value || previousLoading.value) {
    return null
  }
  
  if (previousData.value === null || previousData.value === undefined) {
    return null
  }
  
  const current = planCount.value
  const previous = previousCount.value
  
  // If previous is 0 and current is also 0, don't show comparison
  if (previous === 0 && current === 0) return null
  
  // If previous is 0 but current has value, show as 100% increase
  if (previous === 0 && current > 0) {
    return 100
  }
  
  // Calculate percentage change: ((current - previous) / previous) * 100
  // Normal calculation - color inversion is handled by inverted prop in ComparisonChip
  const change = ((current - previous) / previous) * 100
  const rounded = Math.round(change * 10) / 10
  
  return rounded
})

// Format previous count for display
const formattedPreviousCount = computed(() => {
  if (previousCount.value === null || previousCount.value === undefined) return null
  return previousCount.value.toString()
})

// Calculate number of rows to display based on widget height (64px rows; leave room for "View more")
// Pattern: 2-row→2 slots, 3-row→4, 4-row→6, 5-row→8, 6-row→10... i.e. 2*(rowSpan-1)
const maxRowsToDisplay = computed(() => {
  if (props.rowSpan === 1) return 0
  return 2 * (props.rowSpan - 1)
})

// Check if there are more plans than we can display (only show when rowSpan > 1)
const hasMorePlans = computed(() => {
  return props.rowSpan > 1 && plans.value.length > maxRowsToDisplay.value
})

// Calculate remaining count
const remainingPlansCount = computed(() => {
  if (!hasMorePlans.value) return 0
  return plans.value.length - displayPlans.value.length
})

// Display plans ending soon based on widget height (leave room for "more" row if needed)
const displayPlans = computed(() => {
  if (maxRowsToDisplay.value === 0) {
    return []
  }
  return plans.value.slice(0, maxRowsToDisplay.value)
})

// Format plan end time for display
const formatPlanTime = (plan) => {
  if (!plan.endTimestamp) return ''
  const date = new Date(plan.endTimestamp)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const planDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  
  const diffTime = planDate - today
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return 'Ends today'
  } else if (diffDays === 1) {
    return 'Ends tomorrow'
  } else if (diffDays > 0) {
    return `Ends in ${diffDays} days`
  } else {
    return `Ended ${Math.abs(diffDays)} days ago`
  }
}

// Get initials for placeholder
const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    client_id: urlParams.get('client_id') || urlParams.get('clientId') || '',
    canvasUUID: urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  }
}

// Handle "View all" click - navigate to /plans via parent window
const { guard } = useTrialModeGuard()
const handleViewAllClick = () => {
  const params = getUrlParams()
  const finalCanvasUUID = params.canvasUUID || "default-canvas-uuid"
  const dates = getDateRangeDates(dateRange.value)
  const navigationUrl = `/plans?end_date_from=${dates.from}&end_date_to=${dates.to}`

  const message = {
    command: "ReplaceUrl",
    payload: {
      client_id: params.client_id || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }

  try {
    window.parent.postMessage(message, "*")
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*")
    }
  } catch (e) {
    console.error('❌ Error posting ReplaceUrl message:', e)
  }
}

// Handle individual plan click
const handlePlanClickGuarded = guard((plan) => {
  const params = getUrlParams()
  
  if (!plan.id) {
    console.warn('⚠️ Plan ID not found:', plan)
    return
  }
  
  // Extract plan_id
  let planId = plan.id
  if (planId.includes('-')) {
    const parts = planId.split('-')
    planId = parts[parts.length - 1]
  }
  
  
  // Use ViewDetails command format
  const message = {
    command: "ViewDetails",
    payload: {
      account_plan_id: planId,
      canvasUUID: params.canvasUUID || "xxx"
    }
  }
  
  
  try {
    window.parent.postMessage(message, "*")
  } catch (error) {
    console.error('❌ Error posting message:', error)
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

// Watch for date range changes and refresh data
watch(dateRange, () => {
  refresh(true)
})

onUnmounted(() => {
  cleanup()
  previousCleanup()
})
</script>

<style scoped>
:deep(.plans-ending-soon-widget) {
  cursor: pointer;
  overflow: hidden;
}

:deep(.plans-ending-soon-widget .widget-content) {
  justify-content: flex-start;
  overflow: hidden;
}

.plan-count {
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
}

.count-row {
  display: flex;
  align-items: baseline;
}

.count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
  margin-top: -1px;
}

.count-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
  padding-bottom: 10px;
}

.count-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.widget-content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.widget-content-wrapper.focused {
  position: relative;
}

.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.plans-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
  max-height: none;
}

.plan-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.plan-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.plan-item:last-child {
  border-bottom: none;
}

.plan-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.plan-item-more:hover {
  background-color: transparent;
}

.plan-item-more .plan-row {
  width: 100%;
}

.plan-item-more .plan-row-more {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.plan-item-more .view-more-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  transition: color 0.2s ease;
}

.plan-item-more .view-more-link:hover {
  color: #2563eb;
}

.plan-item-more .plan-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.plan-item-more .plan-name {
  margin-bottom: 0;
}

.plan-item-more .plan-time {
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
}

.plan-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.plan-image {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.plan-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #E8D5E3 0%, #D4C4E8 100%);
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
  font-size: 14px;
  font-size: 11px;
}

.plan-details {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.plan-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
}

.plan-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plan-meta-sep {
  color: rgba(0, 0, 0, 0.87);
  margin: 0 4px;
}

.plan-account-name {
  color: rgba(0, 0, 0, 0.87);
}

.plan-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-plans {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.plan-image-more {
  position: relative;
  width: 36px;
  height: 28px;
  background: transparent;
}

.stacked-images {
  position: relative;
  width: 100%;
  height: 100%;
}

.stacked-image {
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid white;
}

.stacked-image-top {
  top: 0;
  left: 0;
  z-index: 2;
  transform: rotate(-8deg);
}

.stacked-image-bottom {
  top: 0;
  left: 7px;
  z-index: 1;
}

.stacked-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

</style>
